import React from 'react';
import NotFound from '../pages/NotFound';
import type { ImgCloudPageKey } from './pageKeys';
import { IMG_CLOUD_ROUTE_METADATA, resolveImgCloudPageKey } from './routeMetadata';
import type { ImgCloudRouteMetadata } from './routeMetadata';
import { lazyPage } from './lazyPage';
import type { LazyPage } from './lazyPage';

export interface ImgCloudRouteDefinition extends ImgCloudRouteMetadata {
  element: React.ReactElement;
}

// Each page is its own chunk; page CSS stays in main.css via ./routeStyles.
const PAGES: Readonly<Record<ImgCloudPageKey, LazyPage>> = {
  home: lazyPage(() => import(/* webpackChunkName: "page-home" */ '../pages/Home')),
  'web-hosting': lazyPage(() => import(/* webpackChunkName: "page-web-hosting" */ '../pages/WebHosting')),
  'web-hosting-ai-preview': lazyPage(() => import(/* webpackChunkName: "page-web-hosting-ai-preview" */ '../pages/WebHostingAiPreview')),
  'web-hosting-editor': lazyPage(() => import(/* webpackChunkName: "page-web-hosting-editor" */ '../pages/WebHostingEditor')),
  galleries: lazyPage(() => import(/* webpackChunkName: "page-galleries" */ '../pages/Galleries')),
  directories: lazyPage(() => import(/* webpackChunkName: "page-directories" */ '../pages/Directories')),
  'endpoint-list': lazyPage(() => import(/* webpackChunkName: "page-endpoint-list" */ '../pages/EndpointList')),
  international: lazyPage(() => import(/* webpackChunkName: "page-international" */ '../pages/International')),
  'image-categories': lazyPage(() => import(/* webpackChunkName: "page-image-categories" */ '../pages/ImageCategories')),
  monitor: lazyPage(() => import(/* webpackChunkName: "page-monitor" */ '../pages/Monitor')),
  about: lazyPage(() => import(/* webpackChunkName: "page-about" */ '../pages/About')),
  'ai-info': lazyPage(() => import(/* webpackChunkName: "page-ai-info" */ '../pages/AIInfo')),
  faq: lazyPage(() => import(/* webpackChunkName: "page-faq" */ '../pages/Faq')),
  demo: lazyPage(() => import(/* webpackChunkName: "page-demo" */ '../pages/Demo')),
  'image-processing': lazyPage(() => import(/* webpackChunkName: "page-image-processing" */ '../pages/ImageProcessing')),
  'image-watermark': lazyPage(() => import(/* webpackChunkName: "page-image-watermark" */ '../pages/ImageWatermark')),
  'format-convert': lazyPage(() => import(/* webpackChunkName: "page-format-convert" */ '../pages/FormatConvert')),
  'image-compress': lazyPage(() => import(/* webpackChunkName: "page-image-compress" */ '../pages/ImageCompress')),
  'video-to-gif': lazyPage(() => import(/* webpackChunkName: "page-video-to-gif" */ '../pages/VideoToGif')),
  'video-compressor': lazyPage(() => import(/* webpackChunkName: "page-video-compressor" */ '../pages/VideoCompressor')),
  'mp3-compressor': lazyPage(() => import(/* webpackChunkName: "page-mp3-compressor" */ '../pages/Mp3Compressor')),
  'video-to-audio': lazyPage(() => import(/* webpackChunkName: "page-video-to-audio" */ '../pages/VideoToAudio')),
  'ai-generate': lazyPage(() => import(/* webpackChunkName: "page-ai-generate" */ '../pages/AIGenerate')),
  'image-search': lazyPage(() => import(/* webpackChunkName: "page-image-search" */ '../pages/ImageSearch')),
  trash: lazyPage(() => import(/* webpackChunkName: "page-trash" */ '../pages/Trash')),
  invite: lazyPage(() => import(/* webpackChunkName: "page-invite" */ '../pages/Invite')),
  'invite-welcome': lazyPage(() => import(/* webpackChunkName: "page-invite-welcome" */ '../pages/InviteWelcome')),
  'invite-purchase': lazyPage(() => import(/* webpackChunkName: "page-invite-purchase" */ '../pages/InvitePurchase')),
  'encrypted-links': lazyPage(() => import(/* webpackChunkName: "page-encrypted-links" */ '../pages/EncryptedLinks')),
  'sale-links': lazyPage(() => import(/* webpackChunkName: "page-sale-links" */ '../pages/SaleLinks')),
  'sale-codes': lazyPage(() => import(/* webpackChunkName: "page-sale-codes" */ '../pages/SaleLinks/SaleCodes')),
  'sale-buyer': lazyPage(() => import(/* webpackChunkName: "page-sale-buyer" */ '../pages/SaleLinks/SaleBuyer')),
  'sale-account': lazyPage(() => import(/* webpackChunkName: "page-sale-account" */ '../pages/SaleLinks/SaleAccount')),
  'directory-share': lazyPage(() => import(/* webpackChunkName: "page-directory-share" */ '../pages/DirectoryShare')),
};

/**
 * Loads the page chunk for a router-relative pathname (basename already removed).
 * Unknown paths resolve immediately because NotFound is part of the main bundle.
 */
export const preloadImgCloudRoute = (pathname: string): Promise<unknown> => {
  const pageKey = resolveImgCloudPageKey(pathname);
  return pageKey ? PAGES[pageKey].preload() : Promise.resolve();
};

export const IMG_CLOUD_ROUTE_DEFINITIONS: readonly ImgCloudRouteDefinition[] =
  IMG_CLOUD_ROUTE_METADATA.map((metadata) => {
    if (!metadata.pageKey) {
      return { ...metadata, element: <NotFound /> };
    }
    const { Component } = PAGES[metadata.pageKey];
    return { ...metadata, element: <Component /> };
  });
